import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { OidcUserManager } from "../../helpers/OidcSettings";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import HomeOutlinedIcon from "@material-ui/icons/HomeOutlined";
import clsx from "clsx";
import Button from "@material-ui/core/Button";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(1),
    },
    mainContainer: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        width: `calc(100% + ${theme.spacing(2)}px)`,
        margin: -theme.spacing(1),
    },
    gridItem: {
        padding: theme.spacing(1),
        width: "100%",
        [theme.breakpoints.up("sm")]: {
            width: "66.66%",
        },
        [theme.breakpoints.up("lg")]: {
            width: "50%",
        },
    },
    buttonsContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: 16,
        width: `calc(100% + ${theme.spacing(4)}px)`,
        margin: -theme.spacing(2),
    },
    buttonItem: {
        padding: theme.spacing(2),
        textAlign: "center",
    },
    progress: {
        height: 2,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
    },
    linearColorPrimary: {
        backgroundColor: "#abdcda",
    },
    linearBarColorPrimary: {
        backgroundColor: "#d20000",
    },
    paper: {
        position: "relative",
        padding: theme.spacing(4, 1),
        borderRadius: "1rem",
        boxShadow:
            "rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px",
    },
    line: {
        marginTop: theme.spacing(2),
    },
    done: {
        color: theme.palette.success.main,
        fontSize: 80,
    },
    goto: {
        marginRight: theme.spacing(1),
    },
    sup: {
        fontSize: ".6em",
        marginRight: theme.spacing(1),
    },
    currency: {
        fontSize: ".6em",
        fontWeight: 100,
    },
    bal_conner_img: {
        right: 0,
        bottom: 0,
        height: "100%",
        position: "absolute",
    },

    bal_img: {
        color: theme.palette.primary.light,
        fontSize: theme.spacing(7),
        display: "none",
        [theme.breakpoints.up("sm")]: {
            position: "absolute",
            display: "block",
            top: theme.spacing(2),
            right: 8,
            fontSize: theme.spacing(11),
            right: theme.spacing(3),
        },
        filter: "drop-shadow(3px 5px 2px rgb(0 0 0 / 0.1))",
    },
}));

const OrderFin = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(null);
    const [showSuccess] = useState(true);
    const [token, setToken] = useState(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });

        const loadOrder = async () => {
            setIsLoading(true);
            try {
                const userManager = await OidcUserManager();
                const user = await userManager.getUser();

                let userToken = null;
                if (user && user.access_token) {
                    userToken = `Bearer ${user.access_token}`;
                    setToken(userToken);
                }

                const URL = `/api/v1.0/order/confirmed/${id}`;
                const response = await fetch(URL, {
                    headers: { Authorization: userToken },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                setData(responseData);
                
                console.log("GET ORDER AND SET DATA LAYER:", responseData);
                
                const purchaseKey = `purchase_sent_${responseData.DocNum}`;
                
                if (!sessionStorage.getItem(purchaseKey)) {
                    window.dataLayer = window.dataLayer || [];
                    const dataLayerEvent = {
                        event: "trackCustom",
                        name: "purchase",                        
                        // user_type: "consultant" | "vip",
                        ecommerce: {
                            transaction_id: responseData.DocNum,
                            value: Number(responseData.Amount),
                            currency: responseData.CurrencyName,
                            shipping: Number(responseData.AmountCOD) + Number(responseData.AmountDelivery) + Number(responseData.AmountService),
                            items:
                                responseData.Rows?.map((item) => ({
                                    item_id: item.ProductCode,
                                    item_name: item.ProductName,
                                    price: Number(item.Price),
                                    quantity: item.Qty,
                                })) ?? [],
                        },
                    };

                    // console.log("DATA LAYER EVENT:", dataLayerEvent);
                    
                    window.dataLayer.push({ ecommerce: null });                    
                    window.dataLayer.push(dataLayerEvent);
                    sessionStorage.setItem(purchaseKey, "1");
                }

                

            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            loadOrder();
        }
    }, [id]);

    return (
        <div className={classes.root}>
            {isLoading && (
                <LinearProgress
                    className={classes.progress}
                    classes={{
                        colorPrimary: classes.linearColorPrimary,
                        barColorPrimary: classes.linearBarColorPrimary,
                    }}
                />
            )}
            {data && (
                <div className={classes.mainContainer}>
                    <div className={classes.gridItem}>
                        <Paper className={classes.paper}>
                            <Typography variant="h5" align="center">
                                {t("order.ThanksForOrder")}
                            </Typography>
                            <Typography
                                align="center"
                                variant="h6"
                                gutterBottom
                            >
                                № {data.DocNum}
                            </Typography>

                            <Typography
                                variant="h3"
                                align="center"
                                gutterBottom
                            >
                                {parseInt(data.Amount)}
                                <sup className={classes.sup}>
                                    .
                                    {
                                        (Math.round(data.Amount * 100) / 100)
                                            .toFixed(2)
                                            .split(".")[1]
                                    }
                                </sup>
                                <small className={classes.currency}>
                                    {data.CurrencyName}
                                </small>
                            </Typography>

                            <Typography align="center" gutterBottom>
                                {`${
                                    data.Customer.RegistrationCode !== 9999999
                                        ? data.Customer.RegistrationCode
                                        : ""
                                } ${data.RecipientFirstName} ${
                                    data.RecipientLastName
                                } `}
                            </Typography>

                            <Typography
                                align="center"
                                variant="body1"
                                gutterBottom
                            >
                                {data.Customer.Phone}
                                <sup>✆</sup>, {data.Customer.Email}
                                <sup>✉︎</sup>
                            </Typography>

                            {showSuccess && (
                                <div className={classes.buttonsContainer}>
                                    {token ? (
                                        <div className={classes.buttonItem}>
                                            <Button
                                                variant="outlined"
                                                className={classes.goto}
                                                color="primary"
                                                onClick={() => {
                                                    history.push("/order");
                                                }}
                                            >
                                                {t("pay.GoToOrder")}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className={classes.buttonItem}>
                                            <Button
                                                variant="outlined"
                                                className={classes.goto}
                                                color="primary"
                                                onClick={() => {
                                                    history.push("/");
                                                }}
                                                startIcon={<HomeOutlinedIcon />}
                                            >
                                                {t("pay.GoHome")}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <span
                                className={clsx(
                                    "material-icons",
                                    classes.bal_img
                                )}
                            >
                                task_alt
                            </span>
                            <img
                                className={classes.bal_conner_img}
                                alt="triangle background"
                                src="/assets/images/triangle-light.png"
                            />
                        </Paper>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderFin;
